import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, Col, DataTable, PageHeader, Row, Tab, Tabs } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  PO_TYPE_LABELS,
  PoConsumptionEffort,
  PurchaseOrder,
  PurchaseOrderEvent,
  PurchaseOrderLineItem,
  WORK_INSTRUCTIONS_REVIEWED,
  WORK_INSTRUCTIONS_REVIEWED_VALUES,
  WorkInstructionsReviewed,
  isWorkInstructionsOutstanding,
  purchaseOrderService,
} from '../../../services/purchaseOrderService';
import { RESOURCE_TYPE_LABELS, ResourceType } from '../../../services/vendorService';
import { currency, dateTime, shortDate } from '../../vendors/utils/format';
import ConsumptionBar from '../components/ConsumptionBar';
import PoConsumptionEffortsTable from '../components/PoConsumptionEffortsTable';

/** Event types are stored as stable slugs; these are what a person reads in the timeline. */
const EVENT_LABELS: Record<string, string> = {
  created: 'Created',
  number_reserved: 'PO number reserved',
  imported: 'Imported',
  amount_changed: 'Amount changed',
  activated: 'Activated',
  deactivated: 'Deactivated',
  line_items_changed: 'Line items changed',
  work_instructions_reviewed: 'Work instructions reviewed',
  revised: 'Revised by a later submission',
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="tw-mb-3">
    <div className="tw-text-sm tw-text-content-muted">{label}</div>
    <div className="tw-text-content">{children}</div>
  </div>
);

/** HE-P05 — open a PO to see its header, line items (each with its own task) and consumption. */
const PurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [events, setEvents] = useState<PurchaseOrderEvent[]>([]);
  const [efforts, setEfforts] = useState<PoConsumptionEffort[]>([]);
  const [effortsLoading, setEffortsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const canManageStatus = hasPermission('po:status_manage');
  const canViewHistory = hasPermission('po:history_view');
  const canManageWorkInstructions = hasPermission('po:work_instructions_manage');
  const [savingWorkInstructions, setSavingWorkInstructions] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    // A non-numeric path segment used to reach the API as `/purchase-orders/NaN`. Fail here
    // with the same message a missing PO gets, and skip the pointless request.
    const poId = Number.parseInt(id, 10);
    if (!Number.isFinite(poId)) {
      setLoading(false);
      setError('Purchase order not found');
      setEfforts([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setEfforts([]);
      const data = await purchaseOrderService.getPurchaseOrderById(poId);
      setPo(data);
      setEffortsLoading(true);
      try {
        const matching = await purchaseOrderService.getConsumptionEfforts(poId);
        setEfforts(matching);
      } catch {
        setEfforts([]);
      } finally {
        setEffortsLoading(false);
      }
      if (canViewHistory) {
        // History is a separate permission, so a viewer without it still gets the page
        // rather than an error — the tab simply is not offered.
        const history = await purchaseOrderService.getEvents(poId);
        setEvents(history);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  }, [id, canViewHistory]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleStatus = async () => {
    if (!po) return;
    try {
      setActionError(null);
      const next = po.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await purchaseOrderService.setStatus(po.id, next);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  /**
   * Reloads rather than patching state locally, so the History tab picks up the event the
   * server wrote in the same transaction. Without that the answer would change on screen while
   * the timeline still showed the old one.
   */
  const handleSetWorkInstructions = async (value: WorkInstructionsReviewed) => {
    if (!po) return;
    try {
      setActionError(null);
      setSavingWorkInstructions(true);
      await purchaseOrderService.setWorkInstructions(po.id, value);
      await load();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to update the work instructions review'
      );
    } finally {
      setSavingWorkInstructions(false);
    }
  };

  if (loading) return <div className="tw-p-4">Loading purchase order…</div>;
  if (error) return <div className="tw-p-4 tw-text-danger">Error: {error}</div>;
  if (!po) return <div className="tw-p-4">Purchase order not found.</div>;

  // One answer per purchase order - the requisition form asks it once per submission.
  const workInstructions = po.workInstructionsReviewed;
  const workInstructionsLocked = workInstructions === WORK_INSTRUCTIONS_REVIEWED.YES;

  const lineItemColumns: DataTableColumn<PurchaseOrderLineItem>[] = [
    {
      key: 'resource',
      header: 'Resource',
      mobile: 'title',
      cell: (li) => li.resource?.name ?? li.description ?? '–',
    },
    {
      key: 'resourceType',
      header: 'Type',
      cell: (li) =>
        li.resource
          ? RESOURCE_TYPE_LABELS[li.resource.resourceType as ResourceType] ?? li.resource.resourceType
          : '–',
    },
    {
      key: 'task',
      header: 'Task',
      // Task lives on the line, not the header: one PO routinely spans several tasks, which a
      // PO-level list could show but never attribute to the right line.
      cell: (li) => (li.task ? li.task.taskNumber ?? String(li.task.id) : '–'),
    },
    { key: 'quantity', header: 'Quantity', align: 'right', cell: (li) => li.quantity },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      // What the requisition asked for on this line. Blank rather than $0.00 when the line
      // never carried one - an imported line and a zero-value line are different facts.
      cell: (li) => (typeof li.amount === 'number' ? currency(li.amount) : '–'),
    },
    { key: 'unit', header: 'Unit', cell: (li) => li.unit ?? li.resource?.unit ?? '–' },
    {
      key: 'fixedPrice',
      header: 'Pricing',
      cell: (li) =>
        li.resource?.isFixedPrice ? <Badge bg="secondary">Fixed price</Badge> : <span>Rate + markup</span>,
    },
  ];

  return (
    <div className="purchase-order-detail-page tw-p-4">
      <PageHeader
        title={po.poNumber}
        actions={[
          {
            key: 'back',
            label: 'Back to list',
            icon: 'bi-arrow-left',
            intent: 'secondary',
            onClick: () => navigate('/purchase-orders'),
          },
          ...(canManageStatus && po.status !== 'RESERVED'
            ? [
                {
                  key: 'toggle',
                  label: po.status === 'ACTIVE' ? 'Deactivate' : 'Activate',
                  icon: po.status === 'ACTIVE' ? 'bi-x-circle' : 'bi-check-circle',
                  intent: (po.status === 'ACTIVE' ? 'danger' : 'primary') as 'danger' | 'primary',
                  onClick: handleToggleStatus,
                },
              ]
            : []),
        ]}
      />

      {actionError && (
        <div className="tw-mb-3 tw-text-danger" role="alert">
          {actionError}
        </div>
      )}

      <Card className="tw-mb-3">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Field label="Status">
                {po.status === 'ACTIVE' && <Badge bg="success">Active</Badge>}
                {po.status === 'INACTIVE' && <Badge bg="secondary">Inactive</Badge>}
                {po.status === 'RESERVED' && <Badge bg="warning">Reserved – not yet raised</Badge>}
              </Field>
              <Field label="Vendor">{po.vendor?.name ?? '–'}</Field>
              <Field label="Type">{PO_TYPE_LABELS[po.type] ?? po.type}</Field>
            </Col>
            <Col md={3}>
              <Field label="Description">{po.description || '–'}</Field>
              <Field label="Amount">{currency(po.amount)}</Field>
              <Field label="Reference">{po.reference || '–'}</Field>
            </Col>
            <Col md={3}>
              <Field label="Service Area">{po.serviceArea?.name ?? '–'}</Field>
              <Field label="Foreman Area">{po.foremanArea?.name ?? '–'}</Field>
              <Field label="Contract Area">
                {po.contractArea ? `${po.contractArea.name}` : '–'}
              </Field>
            </Col>
            <Col md={3}>
              <Field label="Requested By">
                {po.requestedBy
                  ? `${po.requestedBy.name} ${po.requestedBy.lastName}`
                  : po.requestedByName || '–'}
              </Field>
              <Field label="Request Date">{shortDate(po.requestDate)}</Field>
              <Field label="Submission ID">{po.submissionId ?? '–'}</Field>
              <Field label="Purchase Reference">{po.purchaseReference || '–'}</Field>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Field label="Work Instructions Reviewed">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                  {workInstructionsLocked ? (
                    <span className="tw-text-success">
                      <i className="bi bi-check-circle-fill tw-mr-1" />
                      {workInstructions}
                    </span>
                  ) : isWorkInstructionsOutstanding(workInstructions) ? (
                    <Badge bg="warning">{workInstructions}</Badge>
                  ) : (
                    <span>{workInstructions}</span>
                  )}

                  {/*
                    Once the PO is "Yes" the buttons disappear entirely. The server refuses the
                    change anyway (409 WORK_INSTRUCTIONS_LOCKED), but a button that always errors
                    is worse than no button - the lock should be visible, not just enforced.
                  */}
                  {canManageWorkInstructions &&
                    !workInstructionsLocked &&
                    WORK_INSTRUCTIONS_REVIEWED_VALUES.filter((v) => v !== workInstructions).map(
                      (v) => (
                        <Button
                          key={v}
                          size="sm"
                          variant="outline-secondary"
                          disabled={savingWorkInstructions}
                          onClick={() => handleSetWorkInstructions(v)}
                        >
                          {v === WORK_INSTRUCTIONS_REVIEWED.YES ? 'Mark reviewed' : `Set "${v}"`}
                        </Button>
                      )
                    )}
                </div>
              </Field>
            </Col>
          </Row>

        </Card.Body>
      </Card>

      <Tabs defaultActiveKey="lineItems" id="po-detail-tabs">
        <Tab eventKey="lineItems" title={`Line Items (${po.lineItems?.length ?? 0})`}>
          <div className="tw-pt-3">
            <DataTable
              columns={lineItemColumns}
              data={po.lineItems ?? []}
              rowKey={(li) => li.id}
              framed
              emptyMessage="No line items on this purchase order."
            />
          </div>
        </Tab>

        <Tab eventKey="consumption" title="Consumption">
          <div className="tw-pt-3">
            <Card>
              <Card.Body>
                {/*
                  Value booked against the PO, from efforts carrying its number (HE-P07). The bar
                  keeps approved and submitted apart deliberately: one is payable, the other is
                  work that has happened but has not cleared form approval. A single "committed"
                  figure would have to pick one meaning and be wrong about the other.

                  `pending` is still honoured — if the source ever cannot compute, this says so
                  rather than rendering a confident zero.
                */}
                <Field label="PO Value">{currency(po.consumption?.amount ?? po.amount)}</Field>
                <div className="tw-mt-3">
                  <ConsumptionBar consumption={po.consumption} />
                </div>
              </Card.Body>
            </Card>
            <div className="tw-mt-3">
              <h3 className="tw-mb-2 tw-text-base tw-font-semibold tw-text-content">Efforts consuming this PO</h3>
              <PoConsumptionEffortsTable efforts={efforts} loading={effortsLoading} />
            </div>
          </div>
        </Tab>

        {canViewHistory && (
          <Tab eventKey="history" title={`History (${events.length})`}>
            <div className="tw-pt-3">
              <Card>
                <Card.Body>
                  {events.length === 0 ? (
                    <p className="tw-text-content-muted tw-mb-0">No events recorded yet.</p>
                  ) : (
                    <ul className="tw-list-none tw-p-0 tw-m-0">
                      {events.map((ev) => (
                        <li
                          key={ev.id}
                          className="tw-flex tw-gap-3 tw-py-2 tw-border-b tw-border-line last:tw-border-0"
                        >
                          <div className="tw-text-content-muted tw-text-sm tw-w-48 tw-shrink-0">
                            {dateTime(ev.occurredAt)}
                          </div>
                          <div>
                            <div className="tw-font-medium">
                              {EVENT_LABELS[ev.eventType] ?? ev.eventType}
                            </div>
                            <div className="tw-text-sm tw-text-content-muted">
                              {ev.actor ? `${ev.actor.name} ${ev.actor.lastName}` : 'System'}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card.Body>
              </Card>
            </div>
          </Tab>
        )}
      </Tabs>
    </div>
  );
};

export default PurchaseOrderDetailPage;
